import { useRef, useCallback } from 'react';
import { SecondaryDeviceFullDto } from '@/lib/api/types.gen';
import { updateSecondaryDevice } from '@/lib/api/devices/devices';

export const useNVRUpdateQueue = () => {
  // NVR update queue to prevent race conditions
  const nvrUpdateQueuesRef = useRef<Map<string, Promise<any>>>(new Map());
  
  const queuedNVRUpdate = useCallback(async (
    nvrId: string, 
    secondaryDevices: SecondaryDeviceFullDto[],
    updateFn: (currentDevices: SecondaryDeviceFullDto[]) => SecondaryDeviceFullDto[]
  ): Promise<void> => {
    // If there's already an update in progress for this NVR, wait for it to complete
    const existingUpdate = nvrUpdateQueuesRef.current.get(nvrId);
    if (existingUpdate) {
      await existingUpdate;
    }

    // Create a new update promise for this NVR
    const updatePromise = (async () => {
      try {
        // Apply the update function to get new devices
        const updatedDevices = updateFn(secondaryDevices);

        // Call the API
        await updateSecondaryDevice({
          deviceId: nvrId,
          primaryDeviceId: nvrId,
          devices: updatedDevices
        });
      } finally {
        // Remove this update from the queue when done
        nvrUpdateQueuesRef.current.delete(nvrId);
      }
    })();

    // Store the promise in the queue
    nvrUpdateQueuesRef.current.set(nvrId, updatePromise);
    
    // Wait for completion
    await updatePromise;
  }, []);

  return { queuedNVRUpdate };
};